import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Paper, Typography, Box, Checkbox } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { glassTooltipContentStyle, glassTooltipItemStyle, glassTooltipLabelStyle } from '../../styles/chartTooltip';

const EU_PTD_RULE_THRESHOLD = 5;
const TREND_SERIES_ORDER: Record<string, number> = {
  meanHourly: 0,
  medianHourly: 1,
  meanBonus: 2,
  medianBonus: 3,
};

const TREND_SERIES = [
  { key: 'meanHourly', name: 'Mean Hourly Gap', color: '#303EB3', isBonus: false },
  { key: 'medianHourly', name: 'Median Hourly Gap', color: '#848FF0', isBonus: false },
  { key: 'meanBonus', name: 'Mean Bonus Gap', color: '#F06A16', isBonus: true },
  { key: 'medianBonus', name: 'Median Bonus Gap', color: '#FFA364', isBonus: true },
] as const;

type TrendSeriesKey = typeof TREND_SERIES[number]['key'];
type TrendSeriesVisibility = Record<TrendSeriesKey, boolean>;

const DEFAULT_VISIBLE_TREND_SERIES: TrendSeriesVisibility = {
  meanHourly: true,
  medianHourly: true,
  meanBonus: true,
  medianBonus: true,
};

const TREND_TOOLTIP_ITEM_SORTER = (item: any) => TREND_SERIES_ORDER[item.dataKey as string] ?? 99;
const TREND_TOOLTIP_VALUE_FORMATTER = (value: number | undefined) => value === undefined ? 'N/A' : `${value}%`;
const TREND_CHART_MARGIN = { top: 6, right: 30, left: 0, bottom: 0 };
const TREND_LEGEND_WRAPPER_STYLE = { left: 25, right: 36 };

const getVisibleTrendSeriesCount = (visibleTrendSeries: TrendSeriesVisibility): number =>
  Object.values(visibleTrendSeries).filter(Boolean).length;

const TrendLines: React.FC<{ visibleTrendSeries: TrendSeriesVisibility }> = React.memo(
  ({ visibleTrendSeries }) => (
    <>
      {TREND_SERIES.map((series) => (
        <Line
          key={series.key}
          type="monotone"
          dataKey={series.key}
          name={series.name}
          hide={!visibleTrendSeries[series.key]}
          stroke={series.color}
          strokeOpacity={0.9}
          strokeWidth={2}
          strokeDasharray={series.isBonus ? '5 5' : undefined}
          strokeLinecap={series.isBonus ? 'round' : 'butt'}
        />
      ))}
    </>
  )
);

const CompanyTrendChart: React.FC<{ company: any }> = ({ company }) => {
  const [visibleTrendSeries, setVisibleTrendSeries] = useState<TrendSeriesVisibility>(DEFAULT_VISIBLE_TREND_SERIES);
  const [highlightZeroTrendBaseline, setHighlightZeroTrendBaseline] = useState<boolean>(false);

  useEffect(() => {
    setVisibleTrendSeries(DEFAULT_VISIBLE_TREND_SERIES);
    setHighlightZeroTrendBaseline(false);
  }, [company]);

  const toggleTrendSeries = useCallback((seriesKey: TrendSeriesKey) => {
    setVisibleTrendSeries((prev) => {
      const isCurrentlyVisible = prev[seriesKey];
      const visibleCount = getVisibleTrendSeriesCount(prev);
      if (isCurrentlyVisible && visibleCount === 1) {
        return prev;
      }
      return {
        ...prev,
        [seriesKey]: !isCurrentlyVisible,
      };
    });
  }, []);

  const handleToggleZeroTrendBaseline = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setHighlightZeroTrendBaseline(event.target.checked);
  }, []);

  const companyTrendData = useMemo(() => {
    if (!company) return [];
    return [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024]
      .map((year) => ({
        year,
        meanHourly: company[`${year}_DiffMeanHourlyPercent`],
        medianHourly: company[`${year}_DiffMedianHourlyPercent`],
        meanBonus: company[`${year}_DiffMeanBonusPercent`],
        medianBonus: company[`${year}_DiffMedianBonusPercent`],
      }))
      .filter((item) =>
        item.meanHourly !== undefined || item.medianHourly !== undefined ||
        item.meanBonus !== undefined || item.medianBonus !== undefined
      );
  }, [company]);

  const activeTrendSeriesKeys = useMemo(
    () => TREND_SERIES.filter((series) => visibleTrendSeries[series.key]).map((series) => series.key),
    [visibleTrendSeries]
  );

  const trendSeriesValues = useMemo(() => {
    if (activeTrendSeriesKeys.length === 0) return [];
    return companyTrendData
      .flatMap((point) => activeTrendSeriesKeys.map((seriesKey) => point[seriesKey]))
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  }, [companyTrendData, activeTrendSeriesKeys]);

  const { trendDomain, trendYAxisTicks } = useMemo(() => {
    const minValue = trendSeriesValues.length > 0 ? Math.min(...trendSeriesValues, 0) : -10;
    const maxValue = trendSeriesValues.length > 0 ? Math.max(...trendSeriesValues, 0) : 10;

    if (minValue === maxValue) {
      const ticks = Array.from(new Set([minValue, 0])).sort((a, b) => a - b);
      return {
        trendDomain: [ticks[0], ticks[ticks.length - 1]] as [number, number],
        trendYAxisTicks: ticks,
      };
    }

    const span = maxValue - minValue;
    const targetIntervals = 5;
    const step = Math.max(1, Math.ceil(span / targetIntervals));
    const start = Math.floor(minValue / step) * step;
    const end = Math.ceil(maxValue / step) * step;
    const ticks: number[] = [];
    for (let tick = start; tick <= end; tick += step) {
      ticks.push(tick);
    }
    ticks.push(0);
    const sortedTicks = Array.from(new Set(ticks)).sort((a, b) => a - b);
    return {
      trendDomain: [sortedTicks[0], sortedTicks[sortedTicks.length - 1]] as [number, number],
      trendYAxisTicks: sortedTicks,
    };
  }, [trendSeriesValues]);

  const positiveZoneStart = highlightZeroTrendBaseline ? EU_PTD_RULE_THRESHOLD : 0;
  const negativeZoneEnd = highlightZeroTrendBaseline ? -EU_PTD_RULE_THRESHOLD : 0;
  const showMenZone = trendDomain[1] > positiveZoneStart;
  const showWomenZone = trendDomain[0] < negativeZoneEnd;
  const showMenLabel = trendDomain[1] > 0;
  const showWomenLabel = trendDomain[0] < 0;
  const neutralBandTop = Math.min(trendDomain[1], EU_PTD_RULE_THRESHOLD);
  const neutralBandBottom = Math.max(trendDomain[0], -EU_PTD_RULE_THRESHOLD);
  const showNeutralBand = highlightZeroTrendBaseline && neutralBandBottom < neutralBandTop;

  const renderTrendYAxisTick = useCallback((props: any) => {
    const { x, y, payload } = props;
    const value = payload?.value;
    const isZero = value === 0;
    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor="end"
        fill={isZero && highlightZeroTrendBaseline ? '#02D68F' : '#666'}
        fontWeight={isZero ? 700 : 400}
      >
        {value}%
      </text>
    );
  }, [highlightZeroTrendBaseline]);

  const renderTrendZoneLabel = useCallback((label: string) => (props: any) => {
    const viewBox = props?.viewBox;
    if (!viewBox) return <g />;
    return (
      <text
        x={viewBox.x + (viewBox.width / 2)}
        y={viewBox.y + (viewBox.height / 2)}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#9ca3af"
        fontWeight="600"
        fontSize="12"
        letterSpacing="1.4px"
        pointerEvents="none"
      >
        {label}
      </text>
    );
  }, []);

  const menTrendZoneLabel = useMemo(() => renderTrendZoneLabel('FAVORS MEN'), [renderTrendZoneLabel]);
  const womenTrendZoneLabel = useMemo(() => renderTrendZoneLabel('FAVORS WOMEN'), [renderTrendZoneLabel]);

  const renderTrendLegend = useCallback(() => {
    const visibleCount = getVisibleTrendSeriesCount(visibleTrendSeries);

    return (
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          gap: 16,
          justifyContent: 'center'
        }}
      >
        {TREND_SERIES.map((series) => {
          const isVisible = visibleTrendSeries[series.key];
          const isLockedVisible = isVisible && visibleCount === 1;

          return (
            <li key={series.key} style={{ cursor: 'pointer' }}>
              <button
                type="button"
                onClick={() => toggleTrendSeries(series.key)}
                aria-pressed={isVisible}
                disabled={isLockedVisible}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 999,
                  border: `1px solid ${isVisible ? series.color : '#d1d5db'}`,
                  background: isVisible ? `${series.color}12` : '#f9fafb',
                  cursor: isLockedVisible ? 'not-allowed' : 'pointer',
                  opacity: isLockedVisible ? 0.8 : 1
                }}
              >
                <svg width="18" height="10" aria-hidden="true" style={{ opacity: isVisible ? 1 : 0.45, overflow: 'visible', cursor: 'pointer' }}>
                  <line
                    x1="1"
                    y1="5"
                    x2="17"
                    y2="5"
                    stroke={series.color}
                    strokeWidth="2"
                    strokeDasharray={series.isBonus ? '6 4' : undefined}
                    strokeLinecap="round"
                  />
                </svg>
                <span style={{ color: isVisible ? series.color : '#6b7280', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                  {series.name}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }, [visibleTrendSeries, toggleTrendSeries]);

  if (companyTrendData.length <= 1) {
    return (
      <Paper elevation={3} sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Pay Gap % Over Time</Typography>
        <Typography sx={{ mt: 4 }}>
          Pay gap trend data is not available for this company.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 2, borderRadius: 2, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 8, right: 12 }}>
        <Box
          component="label"
          htmlFor="eu-ptd-5-rule-toggle"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', cursor: 'pointer' }}>
            EU PTD 5% Rule
          </Typography>
          <Checkbox
            id="eu-ptd-5-rule-toggle"
            size="small"
            checked={highlightZeroTrendBaseline}
            onChange={handleToggleZeroTrendBaseline}
            sx={{ p: 0.5, cursor: 'pointer' }}
            inputProps={{ 'aria-label': 'EU PTD 5% Rule' }}
          />
        </Box>
      </Box>
      <Typography variant="h6" align="center" gutterBottom>Pay Gap % Over Time</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={companyTrendData} margin={TREND_CHART_MARGIN}>
          {showMenZone && (
            <ReferenceArea
              y1={positiveZoneStart}
              y2={trendDomain[1]}
              fill="#eff6ff"
            />
          )}
          {showWomenZone && (
            <ReferenceArea
              y1={trendDomain[0]}
              y2={negativeZoneEnd}
              fill="#fdf2f8"
            />
          )}
          {showNeutralBand && (
            <ReferenceArea
              y1={neutralBandBottom}
              y2={neutralBandTop}
              fill="#D7F5E8"
            />
          )}
          {showMenLabel && (
            <ReferenceArea
              y1={0}
              y2={trendDomain[1]}
              fillOpacity={0}
              strokeOpacity={0}
              label={menTrendZoneLabel}
            />
          )}
          {showWomenLabel && (
            <ReferenceArea
              y1={trendDomain[0]}
              y2={0}
              fillOpacity={0}
              strokeOpacity={0}
              label={womenTrendZoneLabel}
            />
          )}
          <CartesianGrid stroke="#e5e7eb" strokeWidth={0.8} vertical={true} horizontal={false} />
          <ReferenceLine y={0} stroke={highlightZeroTrendBaseline ? '#02D68F' : '#e5e7eb'} strokeWidth={0.8} />
          <XAxis dataKey="year" axisLine={false} tickLine={false} />
          <YAxis width={56} axisLine={false} tickLine={false} allowDecimals={false} domain={trendDomain} ticks={trendYAxisTicks} tick={renderTrendYAxisTick} />
          <Tooltip
            formatter={TREND_TOOLTIP_VALUE_FORMATTER}
            contentStyle={glassTooltipContentStyle}
            labelStyle={glassTooltipLabelStyle}
            itemStyle={glassTooltipItemStyle}
            itemSorter={TREND_TOOLTIP_ITEM_SORTER}
          />
          <Legend
            align="center"
            verticalAlign="top"
            height={72}
            wrapperStyle={TREND_LEGEND_WRAPPER_STYLE}
            content={renderTrendLegend}
          />
          <TrendLines visibleTrendSeries={visibleTrendSeries} />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default CompanyTrendChart;
